import type { VoidFunctionComponent } from 'react';
import type { DependencyIssue } from '../../../../../../../shared/dependenciesV2';
import { BadgeColors, icons, TooltipBadge } from '../../../../../generic';
import { LicensingIssueType } from '../../../../../../../typings/enums';
import { LICENSING_ISSUE_TYPES } from '../../../../../../../shared/issuesV2';
import { isIssueOfActiveType } from '../helpers';
import DynamicShowMoreList from '../../../../../generic/DynamicShowMoreList';
import styles from './DetailsRow.module.less';

const IssueColors: Partial<Record<LicensingIssueType, BadgeColors>> = {
  [LicensingIssueType.policy_conflict]: BadgeColors.LightRed,
  [LicensingIssueType.policy_flag]: BadgeColors.LightYellow,
  [LicensingIssueType.unlicensed_dependency]: BadgeColors.LightGray,
};

const IssueNames: Partial<Record<LicensingIssueType, string>> = {
  [LicensingIssueType.policy_conflict]: 'Denied',
  [LicensingIssueType.policy_flag]: 'Flagged',
};

/**
 * Defines licenses which rich data: declared and issue type
 */
type RichLicense = {
  licenseId: string;
  isDeclared: boolean;
  issueType: LicensingIssueType;
};

/**
 * Unlicensed richLicense is a special case for when there is no licenses on the dep
 */
const unlicensedRichLicense: RichLicense = {
  licenseId: 'Unlicensed',
  isDeclared: false,
  issueType: LicensingIssueType.unlicensed_dependency,
};

/**
 * Builds rich license from dep data
 * @param {string} licenseId License ID
 * @param {string[]} declaredLicenses list of declared licenses
 * @param {DependencyIssue[]} issues issues on the dependency
 * @returns {RichLicense} rich license object
 */
const buildRichLicense = (licenseId: string, declaredLicenses: string[], issues: DependencyIssue[]): RichLicense => ({
  licenseId,
  isDeclared: declaredLicenses.includes(licenseId),
  issueType: issues.find((issue) => isIssueOfActiveType(issue, LICENSING_ISSUE_TYPES) && issue.license === licenseId)
    ?.type as LicensingIssueType,
});

/**
 * Sorts RichLicense array by declared first then alphabetical
 * @param {RichLicense} a comparison license
 * @param {RichLicense} b comparison license
 * @returns {number} Comparison result
 */
const sortDeclaredLicenseFirst = (a: RichLicense, b: RichLicense) => {
  if (a.isDeclared && !b.isDeclared) {
    return -1;
  }

  if (b.isDeclared && !a.isDeclared) {
    return 1;
  }

  return a.licenseId.localeCompare(b.licenseId);
};

type LicensingBadgeProps = {
  license: RichLicense;
};

/**
 * License badge displays a license with an icon if it is declared and
 * colors which indicate an issue type
 * @param {LicensingBadgeProps} props Rich License
 * @returns {React.ReactElement} License badge
 */
const LicensingBadge: VoidFunctionComponent<LicensingBadgeProps> = ({ license }) => {
  const isDeclaredIcon = (license.isDeclared && icons.circleStar) || undefined;
  const issueColor = (license.issueType && IssueColors[license.issueType]) || BadgeColors.White;
  const tooltip = `${license.isDeclared ? 'Declared License - ' : ''}${license.licenseId}${license.issueType && IssueNames[license.issueType] ? `, ${IssueNames[license.issueType]}` : ''}`;

  return (
    <TooltipBadge
      color={issueColor}
      icon={isDeclaredIcon}
      id={`license-${license.licenseId}`}
      tooltip={tooltip}
      fullWidth
    >
      {license.licenseId}
    </TooltipBadge>
  );
};

type Props = {
  // License IDs for the dependency
  licenses: string[];
  // Declared licenses for the dependency
  declaredLicenses: string[];
  // Issues on the dependency
  issues: DependencyIssue[];
};

/**
 * License section containing a list of licenses
 * @returns {React.ReactElement} License section
 */
const LicensingSection: VoidFunctionComponent<Props> = ({ licenses, declaredLicenses, issues }) => {
  const richLicenses = licenses
    .map((license) => buildRichLicense(license, declaredLicenses, issues))
    .sort(sortDeclaredLicenseFirst);

  if (richLicenses.length === 0) {
    richLicenses.push(unlicensedRichLicense);
  }

  const licenseElements = richLicenses.map((richLicense) => {
    return <LicensingBadge license={richLicense} key={richLicense.licenseId} />;
  });

  return (
    <div className={styles.licenseList}>
      <DynamicShowMoreList list={licenseElements} lineHeight={27} />
    </div>
  );
};

export default LicensingSection;
